import { LuArrowUp } from 'react-icons/lu';

import { useReducedMotion } from '@mds/shared/hooks/use-reduced-motion';
import { useScrolled } from '@mds/shared/hooks/use-scrolled';
import { cn } from '@mds/shared/lib/utils';

/**
 * A way back up from a long page, mounted once in the root layout so every
 * page has it.
 *
 * Shown only past one screen of scrolling — above that the top is a flick
 * away, and the button would just cover content. On a phone it is a round
 * arrow inside thumb reach; from `lg` it carries its label.
 *
 * Focus goes to the page's heading, so the next Tab starts from the top
 * rather than from a button that has just faded out.
 */
export default function BackToTop() {
  const shown = useScrolled(typeof window === 'undefined' ? 0 : window.innerHeight);
  const reducedMotion = useReducedMotion();

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    const heading = document.querySelector<HTMLElement>('main h1');
    if (heading) {
      // Headings are not focusable by default; -1 makes them a target for
      // script without adding a stop to the tab order.
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  };

  return (
    <button
      type="button"
      onClick={toTop}
      aria-label="Back to top"
      // Out of the tab order and hidden from screen readers while invisible.
      tabIndex={shown ? 0 : -1}
      aria-hidden={!shown}
      className={cn(
        'fixed right-4 bottom-6 z-(--z-sticky) inline-flex size-11 items-center justify-center gap-2 rounded-button border border-hairline bg-surface text-sm font-medium text-ink shadow-lift',
        'transition-[opacity,translate] duration-(--duration-fast) ease-standard motion-reduce:transition-none',
        'hover:border-brand-edge focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        'lg:right-6 lg:w-auto lg:px-4.5',
        shown ? 'opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
      )}
    >
      <LuArrowUp aria-hidden="true" className="size-4 shrink-0" />
      <span className="hidden lg:inline">Back to top</span>
    </button>
  );
}
